import { STATUS_TYPE } from '../../global/constants';
import {
  PullRequest,
  RawFile,
  RawTask,
  RawTimeLogDataInterface,
  SubTasksTimeLogInterface,
  TaskLink
} from '../interfaces/task';
import { rootStore } from '../rootStore';
import { CommentModel, createObservableComment } from './comment';
import { RawComment } from '../interfaces/comment';
import { ProjectTemplateModel } from './projectTemplate';
import { getRawTaskLinks } from '../../services/taskServices';
import { VercelDeployments } from '../interfaces/project';
import { computed, makeObservable, observable } from 'mobx';

export class TaskModel {
  // Common
  id: string;
  name: string;
  description: string;
  isArchived: boolean;
  createdOn: Date;
  dueDate: Date | null;
  estimate: number | null;
  updatedTime: Date | null;
  createdBy: string | undefined;
  updatedBy: string | undefined;

  // Relational
  projectId: string;
  milestoneId: string | null;
  groupId: string | null;
  parentTaskId: string | null;
  companyId: string;
  statusId: string;
  taskSubscribers: string[];
  usersIds: string[];

  // Others
  index: number;
  files: RawFile[];
  comments: CommentModel[];
  labelsIds: string[];
  priority: number | null;
  status: boolean;
  subtaskIndex: number | null;
  taskNumber?: number;
  pullRequests: PullRequest[];
  links: TaskLink[] = [];
  timeLogs?: RawTimeLogDataInterface[];
  subTasksTimeLog: SubTasksTimeLogInterface | null = null;
  recurringInterval: string | null;
  start: string | null;
  vercelDeployments: VercelDeployments[] = [];

  constructor(rawTask: RawTask) {
    makeObservable(this, {
      name: observable,
      description: observable,
      dueDate: observable,
      estimate: observable,
      // updatedTime drives the completed-tasks date filter and recently-completed sort;
      // with merge-style upserts the instance is mutated in place, so this must be
      // observable for those computeds to re-evaluate.
      updatedTime: observable,
      statusId: observable,
      milestoneId: observable,
      groupId: observable,
      // projectId/parentTaskId/index/subtaskIndex must be observable so the
      // computed getters below (and the taskStore lookup indexes) invalidate
      // on local in-place updates, not just on sync replacing the instance.
      projectId: observable,
      parentTaskId: observable,
      index: observable,
      subtaskIndex: observable,
      usersIds: observable,
      labelsIds: observable,
      priority: observable,
      status: observable,
      files: observable,
      timeLogs: observable,
      subTasksTimeLog: observable.ref,
      comments: observable,
      // Mutable fields that can change via a realtime merge upsert and are rendered;
      // must be observable so in-place merges re-render consumers (e.g. vercelCard
      // reads pullRequests). Replacing the instance no longer happens under 'merge'.
      taskSubscribers: observable,
      pullRequests: observable,
      links: observable,
      recurringInterval: observable,
      start: observable,
      // Watch vercel deployments so the UI updates immediately when deploy status changes.
      vercelDeployments: observable,
      users: computed,
      subTasks: computed,
      activeSubTasks: computed,
      completedSubTasks: computed,
      parentTask: computed,
      project: computed,
      labels: computed,
      statusDetails: computed
    });

    // Common
    this.id = rawTask.id;
    this.name = rawTask.name;
    this.description = rawTask.description;
    this.isArchived = rawTask.isArchived;

    // Dates
    this.createdOn = new Date(rawTask.createdOn);
    this.dueDate = rawTask.dueDate ? new Date(rawTask.dueDate) : null;
    this.estimate = rawTask.estimate ?? null;
    this.updatedTime = rawTask.updatedTime ? new Date(rawTask.updatedTime) : null;

    // Relational
    this.createdBy = rawTask.createdBy;
    this.updatedBy = rawTask.updatedBy;
    this.companyId = rawTask.companyId;
    this.projectId = rawTask.projectId;
    this.milestoneId = rawTask.milestoneId;
    this.groupId = rawTask.groupId;
    this.parentTaskId = rawTask.parentTaskId;
    this.statusId = rawTask.statusId;
    this.taskSubscribers = rawTask.taskSubscribers;
    this.usersIds = rawTask.users || [];

    // Others
    this.index = rawTask.index || this.createdOn.getTime();
    this.files = rawTask.files;
    this.comments = rawTask.comments.map((comment) => createObservableComment(comment));
    this.labelsIds = rawTask.labels || [];
    this.priority = rawTask.priority ?? null;
    this.status = rawTask.status;
    this.subtaskIndex = rawTask.subtaskIndex ?? this.createdOn.getTime();
    this.taskNumber = rawTask.taskNumber;
    this.pullRequests = rawTask.pullRequests;
    this.timeLogs = rawTask.timeLogs;
    this.recurringInterval = rawTask.recurringInterval;
    this.start = rawTask.start;
    this.links = rawTask.links?.map(getRawTaskLinks) || [];
    this.vercelDeployments = [];
  }

  get users() {
    return rootStore.workspaceStore.currentWorkspace?.users.filter((user) => this.usersIds.includes(user.id)) || [];
  }

  get subTasks() {
    const subTasks = rootStore.taskStore.tasksByParentId.get(this.id) ?? [];
    return subTasks.slice().sort((a, b) => (b.subtaskIndex ?? 0) - (a.subtaskIndex ?? 0));
  }

  // Split subtasks once per task; recomputes only when this task's subTasks change.
  get activeSubTasks() {
    return this.subTasks.filter((sub) => !sub?.status);
  }

  get completedSubTasks() {
    return this.subTasks.filter((sub) => sub?.status);
  }

  get parentTask(): TaskModel | null {
    if (!this.parentTaskId) return null;
    return rootStore.taskStore.tasksById.get(this.parentTaskId) ?? null;
  }

  get project() {
    return rootStore.projectStore.projectsById.get(this.projectId) ?? null;
  }

  get labels() {
    return this.project?.labelsList.filter((label) => this.labelsIds.includes(label.id)) ?? [];
  }

  get statusDetails() {
    if (!this.project?.statusEnable) {
      return undefined;
    }

    const statusDetails =
      this.project.statusData.find((status) => status['_id'] === this.statusId) ??
      this.project.statusData.find((status) => status.Default);

    return statusDetails;
  }

  hasAssignee(userId: string) {
    return this.usersIds.includes(userId);
  }

  addAssignee(userId: string, isTemplateProject?: boolean) {
    const project = isTemplateProject ? rootStore.projectTemplateStore.currentProjectTemplate : this.project;
    if (project?.multiAssignee) {
      this.usersIds.push(userId);
    } else {
      this.usersIds = [userId];
    }
  }

  setName(name: string) {
    this.name = name;
  }

  setProjectId(projectId: string) {
    this.projectId = projectId;
  }

  removeAssignee(userId: string) {
    this.usersIds = this.usersIds.filter((id) => id !== userId);
  }

  clearAssignees() {
    this.usersIds = [];
  }

  setStatus(newStatus: string | boolean, changeSubTasks?: boolean, projectTemplate?: ProjectTemplateModel) {
    const isStatusEnabled = projectTemplate ? projectTemplate.statusEnable : this.project?.statusEnable;
    if (isStatusEnabled) {
      if (typeof newStatus !== 'string') return;
      const projectStatusData = projectTemplate ? projectTemplate.statusData || [] : this.project?.statusData || [];
      const newStatusObj = projectStatusData?.find((status) => status['_id'] === newStatus);

      if (!newStatusObj) {
        return;
      }

      if (newStatusObj.Type === STATUS_TYPE.COMPLETED) {
        this.status = true;
        if (changeSubTasks) {
          this.subTasks.forEach((subTask) => {
            subTask.statusId = newStatusObj['_id'];
            subTask.status = true;
          });
        }
      } else {
        if (this.status) {
          this.status = false;
        }
      }
      this.statusId = newStatusObj['_id'];
    } else {
      if (typeof newStatus !== 'boolean') return;
      this.status = newStatus;
      if (newStatus && changeSubTasks) {
        this.subTasks.forEach((subTask) => {
          subTask.status = true;
        });
      }
    }
  }

  setGroup(groupId: string | null) {
    this.groupId = groupId;
  }

  setMilestone(milestoneId: string | null) {
    this.milestoneId = milestoneId;
  }

  setDueDate(dueDate: Date | null) {
    this.dueDate = dueDate ? new Date(dueDate) : null;
  }

  setEstimate(estimate: number) {
    this.estimate = estimate;
  }

  addLabel(labelId: string) {
    this.labelsIds.push(labelId);
  }

  removeLabel(labelId: string) {
    this.labelsIds = this.labelsIds.filter((id) => id !== labelId);
  }

  hasLabel(labelId: string) {
    return this.labelsIds.includes(labelId);
  }

  setDescription(description: string) {
    this.description = description;
  }

  setPriority(priority: number) {
    this.priority = priority;
  }

  setComments(comments: RawComment[]) {
    this.comments = comments.map(createObservableComment);
  }

  upsertComment(comment: RawComment) {
    const existingIndex = this.comments.findIndex((c) => c.id === comment.id);
    if (existingIndex >= 0) {
      // For existing comments, just update in place without modifying array order
      this.comments[existingIndex] = createObservableComment(comment);
    } else {
      // For new comments, add to end
      this.comments.push(createObservableComment(comment));
    }
  }

  removeComment(commentId: string) {
    const comment = this.comments.find((comment) => comment.id === commentId);
    if (comment) {
      comment.isArchived = true;
    }
  }

  setParentTask(parentTaskId: string | null) {
    this.parentTaskId = parentTaskId;
  }

  setIndex(index: number) {
    this.index = index;
  }

  setTimeLogs(timeLogs: RawTimeLogDataInterface[]) {
    this.timeLogs = timeLogs;
  }

  setSubTasksTimeLog(subTasksTimeLog: SubTasksTimeLogInterface | null) {
    this.subTasksTimeLog = subTasksTimeLog;
  }

  addLink(link: TaskLink) {
    this.links.push(link);
  }

  removeLink(linkId: string) {
    this.links = this.links.filter((link) => link.id !== linkId);
  }

  updateLink(linkId: string, link: TaskLink) {
    const index = this.links.findIndex((link) => link.id === linkId);
    if (index !== -1) {
      this.links[index] = link;
    }
  }

  setFiles(files: RawFile[]) {
    this.files = files;
  }

  // Lets the task detail response refresh PRs, since sync and realtime updates skip them.
  setPullRequests(pullRequests: PullRequest[]) {
    this.pullRequests = pullRequests;
  }

  setSubtaskIndex(subtaskIndex: number) {
    this.subtaskIndex = subtaskIndex;
  }

  setRecurringInterval(recurringInterval: string) {
    this.recurringInterval = recurringInterval;
  }

  setStart(start: string | null) {
    this.start = start;
  }

  setVercelDeployment(data: VercelDeployments[]) {
    this.vercelDeployments = data;
  }
  // *getGroupName() {
  //   if (!this.groupId) return 'other';
  //   const groups: string = yield fetchGroupName(this.groupId);
  //   return groups;
  // }
}

export const createObservableTask = (rawTask: RawTask) => {
  return new TaskModel(rawTask);
};
